import { Badge, type BadgeProps } from '@ctw/shared/components/badge';
import { Button } from '@ctw/shared/components/button';
import { Heading } from '@ctw/shared/components/heading';
import { APP_TOAST_CONTAINER_DRAWER_ID } from '@ctw/shared/components/toast';
import type { DrawerLayout } from '@ctw/shared/context/drawer-context';
import { ToastProvider } from '@ctw/shared/context/toast-provider';
import { useContainerQuery } from '@ctw/shared/hooks/breakpoints';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type PropsWithChildren, type ReactNode, useRef } from 'react';

export interface DrawerContentProps extends PropsWithChildren {
  layout: DrawerLayout;
  label?: string;
  title?: string;
  subtitle?: string;
  badges?: Array<BadgeProps>;
  isDocked: boolean;
  fullbleed?: boolean;
  close: () => void;
  content: {
    buttons?: ReactNode;
    body: ReactNode;
    footer?: ReactNode;
  };
}

export const DrawerContent = ({
  layout,
  label,
  title,
  subtitle,
  badges,
  close,
  fullbleed = false,
  content,
}: DrawerContentProps) => {
  const footerContentRef = useRef<HTMLDivElement>(null);

  // For padding's sake, watch footer presence
  const { height: footerHeight } = useContainerQuery({ containerRef: footerContentRef });

  return (
    <ToastProvider containerId={APP_TOAST_CONTAINER_DRAWER_ID}>
      <section data-testid="drawer" className={tw`relative flex h-full flex-col`}>
        {(layout.showCloseButton || content.buttons) && (
          <div className={tw`absolute top-0.5 right-0.5 z-navigation-sticky`}>
            {layout.showCloseButton && (
              <Button
                testId="drawer-close-button"
                type="button"
                ariaLabel="Close Drawer"
                variant="circular"
                size="xs"
                className={tw`flex items-center justify-center bg-transparent p-1 text-content-main shadow-none transition-all duration-300 hover:bg-content-subtle/15 active:scale-[.98] active:bg-content-subtle/15`}
                onClick={close}
              >
                <FontAwesomeIcon icon={faXmark} className={tw`h-4 w-4`} />
              </Button>
            )}
            {content.buttons}
          </div>
        )}
        {(label || title || (badges && badges.length > 0)) && (
          <header
            data-testid="drawer-header"
            className={tw`z-navigation flex flex-col border-divider-main border-b p-2 pr-10 transition-all duration-300`}
          >
            <div className={tw`flex items-center justify-between gap-2`}>
              <div className={tw`flex items-center`}>
                {label && (
                  <span
                    data-testid="drawer-label"
                    className={tw`font-semibold text-content-subtle text-xs uppercase`}
                  >
                    {label}
                  </span>
                )}
              </div>
              <div data-testid="drawer-badges" className={tw`flex gap-2`}>
                {badges &&
                  badges.length > 0 &&
                  badges.map((badge) => <Badge key={badge.content} {...badge} size="sm" />)}
              </div>
            </div>
            <div>
              {title && (
                <Heading
                  level="h2"
                  testId="drawer-title"
                  className={tw`font-medium text-background-inverse`}
                >
                  {title}
                </Heading>
              )}
              {subtitle && (
                <div className={tw`text-content-subtle text-sm tracking-wide`}>{subtitle}</div>
              )}
            </div>
          </header>
        )}
        <div
          data-testid="drawer-body"
          className={twx('scroll-shadows flex-1 overflow-y-auto overflow-x-hidden', {
            'p-2': !fullbleed,
          })}
        >
          {content.body}
        </div>
        <footer
          data-testid="drawer-footer"
          className={twx('z-navigation border-divider-main border-t transition-all duration-300', {
            'p-2': content.footer && footerHeight > 0,
            hidden: content.footer === undefined || footerHeight <= 0,
          })}
        >
          <div ref={footerContentRef} className={tw`h-full w-full`}>
            {content.footer}
          </div>
        </footer>
      </section>
    </ToastProvider>
  );
};
